import { useEffect, useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import { Typography } from "@mui/material";
import Button from "@mui/material/Button";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslate } from "../../../../../hooks/useTranslate";
import Preloader from "../../../../../components/Preloader";
import { useStudents } from "../../../../../context/students";
import useStatuses from "../../../../../context/status/useStatuses";
import "./CheckoutStatus.css";

const SuccessIcon = (
    <svg viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M15.4695 0.232963C15.8241 0.561287 15.8454 1.1149 15.5171 1.46949L6.14206 11.5945C5.97228 11.7778 5.73221 11.8799 5.48237 11.8748C5.23253 11.8698 4.99677 11.7582 4.83452 11.5681L0.459523 6.44311C0.145767 6.07557 0.18937 5.52327 0.556912 5.20951C0.924454 4.89575 1.47676 4.93936 1.79051 5.3069L5.52658 9.68343L14.233 0.280522C14.5613 -0.0740672 15.1149 -0.0953599 15.4695 0.232963Z" fill="#6CB237" />
    </svg>
);

const ErrorIcon = (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M1.25628 1.25628C1.59799 0.914573 2.15201 0.914573 2.49372 1.25628L8 6.76256L13.5063 1.25628C13.848 0.914573 14.402 0.914573 14.7437 1.25628C15.0854 1.59799 15.0854 2.15201 14.7437 2.49372L9.23744 8L14.7437 13.5063C15.0854 13.848 15.0854 14.402 14.7437 14.7437C14.402 15.0854 13.848 15.0854 13.5063 14.7437L8 9.23744L2.49372 14.7437C2.15201 15.0854 1.59799 15.0854 1.25628 14.7437C0.914573 14.402 0.914573 13.848 1.25628 13.5063L6.76256 8L1.25628 2.49372C0.914573 2.15201 0.914573 1.59799 1.25628 1.25628Z" fill="white" />
    </svg>
);

const delayInMs = 30_000;

const CheckoutStatus = (props) => {
    const { isProcessing, redirect } = props;

    const { t } = useTranslate();
    const stripe = useStripe();
    const { activeStudent } = useStudents();
    const { isApplicant, refetchGeneralStatuses, refetchRenewalStatus } = useStatuses();

    const studentFullName = `${activeStudent.forename} ${activeStudent.surname}`;

    const navigate = useNavigate();
    const location = useLocation();

    const [isLoading, setIsLoading] = useState(true);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isInProcess, setIsInProcess] = useState(false);
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        if (isProcessing) {
            setIsLoading(false);
            return;
        }
        const clientSecret = new URLSearchParams(window.location.search).get("payment_intent_client_secret");

        if (!stripe || !clientSecret) return;

        const retrieveAndHandle = async (isRetry = false) => {
            try {
                const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

                if (!paymentIntent) {
                    setIsError(true);
                    setIsLoading(false);
                    return;
                }

                const { status } = paymentIntent;

                if (status === "succeeded") {
                    setIsSuccess(true);
                    setIsLoading(false);
                    refetchGeneralStatuses();
                    if (!isApplicant) refetchRenewalStatus();
                } else if (status === "requires_action") {
                    setIsInProcess(true);
                    setIsLoading(false);
                    refetchGeneralStatuses();
                    if (!isApplicant) refetchRenewalStatus();
                } else if (status === "processing") {
                    if (!isRetry) {
                        setTimeout(() => retrieveAndHandle(true), delayInMs);
                    } else {
                        setIsInProcess(true);
                        setIsLoading(false);
                    }
                } else {
                    setIsError(true);
                    setIsLoading(false);
                    refetchGeneralStatuses();
                    if (!isApplicant) refetchRenewalStatus();
                }
            } catch (err) {
                setIsError(true);
                setIsLoading(false);
            }
        };

        retrieveAndHandle();
    }, [stripe, isProcessing, isApplicant]);

    useEffect(() => {
        if (isSuccess && redirect) redirect();
    }, [isSuccess]);

    const onTryAgain = () => {
        navigate(location.pathname, { replace: true });
    };

    return (
        <Preloader isLoading={isLoading}>
            <div className={`checkout-status ${isSuccess ? "checkout-status-success" : ""} ${isError ? "checkout-status-error" : ""}`}>
                <span className={`checkout-status__icon ${isSuccess ? "checkout-status__icon-success" : ""} ${isError ? "checkout-status__icon-error" : ""}`}>
                    {isSuccess ? SuccessIcon : null}
                    {isError ? ErrorIcon : null}
                    {isInProcess || isProcessing ? <HourglassBottomIcon className="text-primary" /> : null}
                </span>
                <div>
                    <Typography variant="h2" component="div" className="">
                        {isSuccess ? t("tuitionBilling.succeededMessage") : null}
                        {isError ? t("tuitionBilling.errorMessage") : null}
                    </Typography>
                    {isSuccess && (
                        <Typography variant="body1" component="div" className="checkout-status__subtext">
                            {t("tuitionBilling.succeededMessageSub", { student: studentFullName })}
                        </Typography>
                    )}
                    {(isInProcess || isProcessing) && (
                        <Typography variant="body1" component="div" className="checkout-status__subtext text-center">
                            {t("tuitionBilling.inProcessMessageSub")}
                        </Typography>
                    )}
                </div>
                {isError && (
                    <Button variant="outlined" className="checkout-status__button" onClick={onTryAgain}>
                        {t("tuitionBilling.tryAgain")}
                    </Button>
                )}
            </div>
        </Preloader>
    );
};

export default CheckoutStatus;
